/**
 * `<NotificationPrefsMatrix/>` — B4.4, the per-event x channel notification matrix
 * (wireframe `V_B44`).
 *
 * Issue #1043 (Track B net-new). "What to nudge me about, where": a per-USER grid
 * of the seven Map events (rows) against the three channels (columns) —
 * IN-APP · EMAIL · DIGEST — each cell a checkbox. A Digest info banner explains
 * the weekly-summary channel; `restore defaults` + `save` sit at the foot (the
 * wireframe has an explicit save, so edits are local until saved).
 *
 * Gated behind the `task_notification_prefs` component key (default OFF, §A10):
 * the parent (`MapSettingsHome`) only opens this when the key is on, and the
 * `RemindersBell` footer links here only when the key is on (else it keeps the
 * honest "no settings screen" popover). The backend enforces the gate too (the
 * routes 404 when off).
 *
 * Honesty (trustable pillar — `feedback_brand_pillars`): EMAIL + DIGEST are STORED
 * preferences only. Delivery for those channels is not built yet (founder
 * revenue-first gating), so the column headers carry a calm "saved for when this
 * channel is available" note — we never imply an email sends today. IN-APP is the
 * live channel.
 *
 * Brand firewall (§A9): "PER-USER · ALWAYS REVERSIBLE" — the matrix is the
 * member's OWN choice, never a per-person scorecard, never tier language, burnout
 * never named. Each person sets their own; the backend resolves the caller's own
 * membership (no `member_id` parameter).
 *
 * Editorial revamp (#1043): theme-aware via `useMapTheme()`. With the editorial
 * flag ON (every team today) it renders the warm `V_B44` table — a `c.bgRecess`
 * header row over a `.t-card`, ink-filled checked cells, the info banner, and the
 * `.t-btn` foot actions. Legacy keeps the same structure with the soft-mint
 * palette. Presentational over the data hooks (data ⟂ view, §A7): it fetches via
 * `useNotificationPrefs` / `useSaveNotificationPrefs` and owns only the local
 * draft + dirty tracking.
 *
 * Isolation: taskmap-local hooks + the editorial theme only — no scheduler import.
 */

import { useEffect, useMemo, useState } from "react";

import { ErrorState, Skeleton } from "../../components/ui";
import {
  type NotificationMatrix,
  useNotificationPrefs,
  useSaveNotificationPrefs,
} from "../api/useNotificationPrefs";
import { useMapTheme } from "../theme/MapThemeContext";
import type { MapTheme } from "../theme/tokens";

/**
 * The calm code defaults, mirroring the backend `DEFAULT_MATRIX` (B4.4) — used by
 * "restore defaults" so a reset is instant + does not need a round-trip. The
 * backend re-resolves on save, so this is purely the local draft seed for reset.
 */
const DEFAULT_MATRIX: NotificationMatrix = {
  mentions: { inapp: true, email: true, digest: false },
  assigned_to_me: { inapp: true, email: true, digest: false },
  my_task_unblocked: { inapp: true, email: false, digest: false },
  target_date_approaching: { inapp: true, email: true, digest: false },
  notes_on_my_tasks: { inapp: true, email: false, digest: false },
  state_changes_on_my_branch: { inapp: false, email: false, digest: true },
  plan_wide_changes: { inapp: false, email: false, digest: true },
};

/** Row copy — the label + the calm sub-line, verbatim from the wireframe `V_B44`. */
const EVENT_COPY: Record<string, { label: string; sub: string }> = {
  mentions: { label: "Mentions", sub: "someone @-mentions you in a note" },
  assigned_to_me: {
    label: "Assigned to me",
    sub: "a task is assigned (or re-assigned) to you",
  },
  my_task_unblocked: {
    label: "My task unblocked",
    sub: "a dependency you were waiting on is resolved",
  },
  target_date_approaching: {
    label: "Target date approaching",
    sub: "48 hours before a target you own",
  },
  notes_on_my_tasks: { label: "Notes on my tasks", sub: "new note on a task you own" },
  state_changes_on_my_branch: {
    label: "State changes on my branch",
    sub: "anyone changes state below your node",
  },
  plan_wide_changes: {
    label: "Plan-wide changes",
    sub: "new outcomes, dependencies, snapshots",
  },
};

const CHANNEL_LABEL: Record<string, string> = {
  inapp: "IN-APP",
  email: "EMAIL",
  digest: "DIGEST",
};

/** Channels whose delivery isn't built yet — labelled honestly (trustable pillar). */
const STORED_ONLY_CHANNELS = new Set(["email", "digest"]);

function copyFor(event: string): { label: string; sub: string } {
  return EVENT_COPY[event] ?? { label: event, sub: "" };
}

function matricesEqual(a: NotificationMatrix, b: NotificationMatrix, events: string[]): boolean {
  for (const ev of events) {
    const ca = a[ev];
    const cb = b[ev];
    if (!ca || !cb) return false;
    if (ca.inapp !== cb.inapp || ca.email !== cb.email || ca.digest !== cb.digest) return false;
  }
  return true;
}

export interface NotificationPrefsMatrixProps {
  teamId: string | undefined;
}

export function NotificationPrefsMatrix({ teamId }: NotificationPrefsMatrixProps) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  const query = useNotificationPrefs(teamId, true);
  const save = useSaveNotificationPrefs(teamId);

  // The server's resolved matrix is the source of truth; `draft` is the local,
  // unsaved edit (the wireframe's explicit save). Re-seed the draft whenever a
  // fresh server matrix lands (initial load + after a successful save).
  const [draft, setDraft] = useState<NotificationMatrix | null>(null);
  useEffect(() => {
    if (query.data) setDraft(query.data.matrix);
  }, [query.data]);

  const events = query.data?.events ?? [];
  const channels = query.data?.channels ?? [];

  const dirty = useMemo(() => {
    if (!draft || !query.data) return false;
    return !matricesEqual(draft, query.data.matrix, events);
  }, [draft, query.data, events]);

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-3" data-testid="notification-prefs-loading">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-56 w-full" />
      </div>
    );
  }

  if (query.isError || !draft) {
    return (
      <ErrorState
        title="Couldn’t load notification settings"
        message="Refresh the page; if it keeps happening, check your connection and try again."
      />
    );
  }

  const toggleCell = (event: string, channel: string) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const cell = prev[event] ?? { inapp: false, email: false, digest: false };
      return {
        ...prev,
        [event]: { ...cell, [channel]: !cell[channel as keyof typeof cell] },
      };
    });
  };

  const restoreDefaults = () => {
    // Only reset the events the server knows about (keeps the grid in sync with
    // the resolved vocabulary). A separate save persists it.
    const next: NotificationMatrix = {};
    for (const ev of events) {
      next[ev] = { ...(DEFAULT_MATRIX[ev] ?? { inapp: false, email: false, digest: false }) };
    }
    setDraft(next);
  };

  const handleSave = () => {
    if (!draft) return;
    save.mutate(draft);
  };

  const cols = `1fr ${channels.map(() => "80px").join(" ")}`;

  return (
    <div
      data-testid="notification-prefs-matrix"
      style={{ fontFamily: editorial ? theme.fonts.sans : undefined }}
    >
      {/* Editorial masthead (`V_B44`): a mono kicker + serif title + the brand
          subtitle. */}
      {editorial && (
        <div className="mb-4">
          <span
            className="text-[10px] uppercase"
            style={{ fontFamily: theme.fonts.mono, color: c.muted, letterSpacing: "0.1em" }}
          >
            Notification settings
          </span>
          <h3
            data-testid="notification-prefs-heading"
            className="mt-1 text-[22px] italic leading-tight"
            style={{ fontFamily: theme.fonts.serif, fontWeight: 400, color: c.ink }}
          >
            What to nudge me about, where
          </h3>
          <span
            className="mt-1 block text-[10px] uppercase"
            style={{ fontFamily: theme.fonts.mono, color: c.faint, letterSpacing: "0.08em" }}
          >
            Per-user · Always reversible
          </span>
        </div>
      )}
      {!editorial && (
        <h3 className="mb-3 text-sm font-bold" style={{ color: "#0C2A2A" }}>
          What to nudge me about, where
        </h3>
      )}

      {/* Header row — EVENT / IN-APP / EMAIL / DIGEST. */}
      <div
        data-testid="notification-prefs-header"
        className="grid items-end gap-4 px-[18px] py-2.5"
        style={{
          gridTemplateColumns: cols,
          backgroundColor: editorial ? c.bgRecess : "#F4F8F6",
          borderRadius: `${theme.radii.md}px ${theme.radii.md}px 0 0`,
        }}
      >
        <span
          className="text-[10px] uppercase"
          style={{ fontFamily: theme.fonts.mono, color: c.muted, letterSpacing: "0.08em" }}
        >
          Event
        </span>
        {channels.map((ch) => (
          <div key={ch} className="text-center" data-testid={`notification-prefs-col-${ch}`}>
            <span
              className="text-[10px] uppercase"
              style={{ fontFamily: theme.fonts.mono, color: c.muted, letterSpacing: "0.08em" }}
            >
              {CHANNEL_LABEL[ch] ?? ch}
            </span>
            {/* Honest column label — email/digest delivery isn't built yet, so
                their cells are saved-for-later (trustable pillar). */}
            {STORED_ONLY_CHANNELS.has(ch) && (
              <span
                className="mt-0.5 block text-[8.5px] uppercase not-italic"
                style={{ fontFamily: theme.fonts.mono, color: c.faint, letterSpacing: "0.05em" }}
                data-testid={`notification-prefs-col-note-${ch}`}
              >
                saved for later
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Rows. */}
      <div
        className="overflow-hidden border"
        style={{
          borderColor: editorial ? c.rule : "#E2E8E5",
          borderTop: 0,
          backgroundColor: editorial ? c.cardBg : "#FFFFFF",
          borderRadius: `0 0 ${theme.radii.lg}px ${theme.radii.lg}px`,
        }}
      >
        {events.map((event, i) => {
          const copy = copyFor(event);
          const cell = draft[event] ?? { inapp: false, email: false, digest: false };
          return (
            <div
              key={event}
              data-testid={`notification-prefs-row-${event}`}
              className="grid items-center gap-4 px-[18px] py-3.5"
              style={{
                borderTop: i === 0 ? undefined : `1px solid ${editorial ? c.rule : "#F1F5F3"}`,
              }}
            >
              <div className="min-w-0">
                <div
                  className="text-[13px] font-medium"
                  style={{ color: editorial ? c.ink : "#0C2A2A" }}
                >
                  {copy.label}
                </div>
                <div className="mt-0.5 text-[11.5px]" style={{ color: c.muted }}>
                  {copy.sub}
                </div>
              </div>
              {channels.map((ch) => (
                <div key={ch} className="text-center">
                  <CheckboxCell
                    theme={theme}
                    checked={Boolean(cell[ch as keyof typeof cell])}
                    onToggle={() => toggleCell(event, ch)}
                    label={`${copy.label} — ${CHANNEL_LABEL[ch] ?? ch}`}
                    testId={`notification-prefs-cell-${event}-${ch}`}
                  />
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Digest info banner (`V_B44`). */}
      <div
        data-testid="notification-prefs-digest-banner"
        className="mt-5 rounded-md px-4 py-3 text-[12px]"
        style={{
          backgroundColor: editorial ? c.bgRecess : "#F4F8F6",
          color: editorial ? c.slate : "#3B342B",
          border: `1px solid ${editorial ? c.rule : "#E2E8E5"}`,
        }}
      >
        <strong style={{ color: editorial ? c.ink : "#0C2A2A" }}>Digest:</strong> a single
        Monday-morning summary instead of per-event pings. Honors the team’s “what changed since you
        last looked” pattern.
      </div>

      {/* Honesty note — email + digest delivery isn't built yet (trustable pillar). */}
      <p
        data-testid="notification-prefs-channel-note"
        className="mt-2 text-[11px] italic"
        style={{ color: c.muted }}
      >
        Email and the weekly digest are saved here and take effect once those delivery channels are
        available. In-app nudges are live now.
      </p>

      {/* Foot actions — restore defaults · save. */}
      <div className="mt-5 flex items-center justify-between">
        <button
          type="button"
          data-testid="notification-prefs-restore"
          onClick={restoreDefaults}
          className="text-[12px] underline-offset-2 transition-opacity hover:opacity-80 hover:underline"
          style={{ color: c.muted }}
        >
          restore defaults
        </button>
        <div className="flex items-center gap-3">
          {save.isError && (
            <span
              data-testid="notification-prefs-save-error"
              className="text-[11px]"
              style={{ color: c.muted }}
            >
              Couldn’t save — try again.
            </span>
          )}
          {!dirty && !save.isPending && save.isSuccess && (
            <span
              data-testid="notification-prefs-saved"
              className="text-[11px]"
              style={{ color: c.muted }}
            >
              Saved.
            </span>
          )}
          <button
            type="button"
            data-testid="notification-prefs-save"
            onClick={handleSave}
            disabled={!dirty || save.isPending}
            className="rounded-md px-4 py-1.5 text-[12px] font-medium transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              backgroundColor: editorial ? c.ink : "#1F7A6E",
              color: editorial ? c.canvas : "#FFFFFF",
            }}
          >
            {save.isPending ? "saving…" : "save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * A single matrix checkbox cell — ink-filled `✓` when checked (wireframe `V_B44`),
 * a hairline empty box when not. A real native `<input type="checkbox">` (kept
 * visually hidden) drives the a11y + keyboard behaviour; the visible box is the
 * styled `<span>` label. Testable via the checkbox role.
 */
function CheckboxCell({
  theme,
  checked,
  onToggle,
  label,
  testId,
}: {
  theme: MapTheme;
  checked: boolean;
  onToggle: () => void;
  label: string;
  testId: string;
}) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  const onColor = editorial ? c.ink : "#1F7A6E";
  return (
    <label className="inline-flex cursor-pointer items-center justify-center">
      <input
        type="checkbox"
        checked={checked}
        aria-label={label}
        data-testid={testId}
        onChange={onToggle}
        className="sr-only"
      />
      <span
        aria-hidden
        className="inline-flex h-[18px] w-[18px] items-center justify-center rounded transition-colors"
        style={{
          backgroundColor: checked ? onColor : "transparent",
          border: `1.5px solid ${checked ? onColor : editorial ? c.rule2 : "#CBD5D0"}`,
          color: editorial ? c.canvas : "#FFFFFF",
        }}
      >
        {checked ? <span className="text-[12px] leading-none">✓</span> : null}
      </span>
    </label>
  );
}
